import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import moment from "moment";

import { formatDate } from "../../../helpers/date";

import { downloadCSV } from "../utils";
import ProjectSummaryByCustomerFilters from "./ProjectSummaryByCustomerFilters";
import ProjectSummaryByCustomerList from "./ProjectSummaryByCustomerList";
import * as codeActions from "../../../actions/codeActions";
import * as reportActions from "../../../actions/reportActions";
import { PROJECT_SUMMARY_COLUMNS } from "./columns";
import { customToast as toast } from "../../../utils";

const CallDetailPage = ({ codes, actions }) => {
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    page: 0,
    pageSize: localStorage.getItem(
      "DEFAULT_PAGE_SIZE_PROJECT_SUMMARY_BY_CUSTOMER"
    )
      ? parseInt(
          localStorage.getItem("DEFAULT_PAGE_SIZE_PROJECT_SUMMARY_BY_CUSTOMER")
        )
      : 10,
    filtered: {},
    sorted: []
  });
  const [data, setData] = useState([]);
  const [pageState, setPageState] = useState(1);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);

  useEffect(() => {
    if (!codes.length) {
      actions.loadCodes();
    }
  }, [actions, codes]);

  useEffect(() => {
    if (codes.length) {
      const projectCategory = codes.filter(
        (code) =>
          code.entity === "Project" &&
          code.field === "project_category" &&
          code.order === 2
      );
      const inProcessStatus = codes.filter(
        (code) =>
          code.entity === "Project" &&
          code.field === "status" &&
          code.order === 1
      );
      setFilters((prev) => ({
        ...prev,
        filtered: {
          ...prev.filtered,
          category: projectCategory.map((option) => option._id),
          project_status: inProcessStatus.map((option) => option._id)
        }
      }));
    }
  }, [codes]);

  const fetchData = async () => {
    const { filtered, sorted, page, pageSize } = filters;

    setLoading(true);
    const clearedFilters = {};
    Object.keys(filtered).forEach((key) => {
      if (
        (Array.isArray(filtered[key]) && filtered[key].length) ||
        (!Array.isArray(filtered[key]) && filtered[key])
      ) {
        clearedFilters[key] = filtered[key];
      }
    });

    const { data: projects, pages } =
      await actions.getProjectSummaryByCustomerReport({
        filtered: clearedFilters,
        isApproval: true,
        page,
        pageSize,
        sorted
      });

    setData(projects);
    setPageState(pages);
    setLoading(false);
  };

  const handleChange = (name) => (value) => {
    setLoading(false);
    if (
      Array.isArray(value) &&
      [
        "category",
        "project_manager",
        "customer",
        "project_status",
        "department",
        "call_type",
        "projects",
        "resource1",
        "resource2"
      ].includes(name)
    ) {
      setFilters((prev) => ({
        ...prev,
        filtered: { ...prev.filtered, [name]: value.map((v) => v.value) }
      }));
    } else {
      setFilters((prev) => ({
        ...prev,
        filtered: { ...prev.filtered, [name]: value }
      }));
    }
  };

  useEffect(() => {
    if ((filters.page, filters.pageSize)) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.page, filters.pageSize]);

  const handlePageSizeChange = (pageSize) => {
    localStorage.setItem(
      "DEFAULT_PAGE_SIZE_PROJECT_SUMMARY_BY_CUSTOMER",
      pageSize
    );
    setFilters((prev) => ({ ...prev, pageSize }));
  };

  const handlePageChange = async (page) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  const handleExport = async () => {
    try {
      setLoading(true);
      const clearedFilters = {};
      Object.keys(filters.filtered).forEach((key) => {
        if (
          (Array.isArray(filters.filtered[key]) &&
            filters.filtered[key].length) ||
          (!Array.isArray(filters.filtered[key]) && filters.filtered[key])
        ) {
          clearedFilters[key] = filters.filtered[key];
        }
      });
      const { data: projects } =
        await actions.getProjectSummaryByCustomerReport({
          filtered: clearedFilters,
          isApproval: true,
          page: null,
          pageSize: null,
          sorted: false
        });
      downloadCSV(
        `ProjectSummaryByCustomer-${formatDate(moment(), "yyyyMMdd")}`,
        projects,
        PROJECT_SUMMARY_COLUMNS,
        "Project Summary By Customer"
      );
    } catch (err) {
      toast.error("Error generating file");
    } finally {
      setLoading(false);
    }
  };

  const handleResetFilters = () => {
    const projectCategory = codes.filter(
      (code) =>
        code.entity === "Project" &&
        code.field === "project_category" &&
        code.order === 2
    );
    const inProcessStatus = codes.filter(
      (code) =>
        code.entity === "Project" && code.field === "status" && code.order === 1
    );
    setFilters((prev) => ({
      ...prev,
      filtered: {
        category: projectCategory.map((option) => option._id),
        project_status: inProcessStatus.map((option) => option._id)
      }
    }));
    setData([]);
  };

  return (
    <div id="project-summary-page" className="report-page">
      <ProjectSummaryByCustomerFilters
        codes={codes}
        filters={filters}
        customers={filteredCustomers}
        setCustomers={setFilteredCustomers}
        projects={filteredProjects}
        setProjects={setFilteredProjects}
        onChange={handleChange}
        fetchData={fetchData}
        onExportClick={handleExport}
        handleResetFilters={handleResetFilters}
        exportDisabled={loading || !data?.length}
      />
      <ProjectSummaryByCustomerList
        data={data}
        fetchData={fetchData}
        filtered={filters}
        loading={loading}
        pages={pageState}
        onPageSizeChange={handlePageSizeChange}
        onPageChange={handlePageChange}
        onSortedChange={() => null}
        page={filters.page}
        sorted={filters.sorted}
        pageSize={filters.pageSize}
        defaultPageSize={10}
        onExport={handleExport}
      />
    </div>
  );
};

CallDetailPage.propTypes = {
  codes: PropTypes.array,
  actions: PropTypes.object
};

const mapStatesToProps = (state) => ({
  codes: state.reducers.codes
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      ...codeActions,
      ...reportActions
    },
    dispatch
  )
});

export default connect(mapStatesToProps, mapDispatchToProps)(CallDetailPage);
